/**
 * ファイル単位アップロードの FIFO スケジューラ（DOM / API 非依存）。
 *
 * Issue #88: 同時に走らせるファイル数を上限付きで制御し、
 * 1 件 settle（成功 / 失敗 / キャンセル）したら次を開始する。
 */

/** 同時にアップロードを開始できるファイル数（Issue #88 決定事項） */
export const MAX_CONCURRENT_FILE_UPLOADS = 2

export type QueueHandle = {
  /** 未開始ならキューから除去して true。開始済みなら何もせず false */
  cancel(): boolean
}

type PendingEntry = {
  task: () => Promise<void>
  onError: (error: unknown) => void
  cancelled: boolean
  started: boolean
}

export function createUploadQueue(maxConcurrent: number) {
  const pending: PendingEntry[] = []
  let active = 0

  function pump(): void {
    while (active < maxConcurrent && pending.length > 0) {
      const entry = pending.shift()
      if (!entry || entry.cancelled) continue

      entry.started = true
      active += 1
      void entry
        .task()
        .catch(entry.onError)
        .finally(() => {
          active -= 1
          pump()
        })
    }
  }

  return {
    enqueue(task: () => Promise<void>, onError: (error: unknown) => void): QueueHandle {
      const entry: PendingEntry = { task, onError, cancelled: false, started: false }
      pending.push(entry)
      pump()

      return {
        cancel(): boolean {
          if (entry.cancelled || entry.started) return false
          entry.cancelled = true
          const idx = pending.indexOf(entry)
          if (idx >= 0) {
            pending.splice(idx, 1)
            return true
          }
          return false
        },
      }
    },

    /** 未開始タスクを全除去し、除去された数を返す（unmount 用）。実行中は継続 */
    clearPending(): number {
      const removed = pending.length
      pending.length = 0
      return removed
    },
  }
}
